import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DEMO_ACCOUNTS } from '../pages/DemoPage';
import { ChevronDown, RefreshCw, X } from 'lucide-react';

export default function DemoBar() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const isDemo = localStorage.getItem('demo_mode') === 'true';
  if (!isDemo || !user || dismissed) return null;

  const currentAccount = DEMO_ACCOUNTS.find(a => a.role === user.role);

  const switchRole = async (account) => {
    if (account.role === user.role) { setOpen(false); return; }
    setSwitching(account.role);
    try {
      logout();
      await new Promise(r => setTimeout(r, 100));
      await login(account.email, account.password);
      navigate('/dashboard');
      setOpen(false);
    } catch (e) {
    }
    setSwitching(null);
  };

  const exitDemo = () => {
    localStorage.removeItem('demo_mode');
    logout();
    navigate('/demo');
  };

  const Icon = currentAccount?.icon;

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] print:hidden" data-testid="demo-bar">
      <div className="bg-slate-900 text-white px-4 py-2 flex items-center justify-between text-sm">
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-slate-400 hidden sm:inline">DEMO MODE</span>
          <div className="relative">
            <button
              onClick={() => setOpen(!open)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 transition-colors"
              data-testid="demo-role-switcher"
            >
              {Icon && <Icon size={14} />}
              <span className="font-medium text-xs">{currentAccount?.label || user.role}</span>
              <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border z-20 overflow-hidden" data-testid="demo-role-dropdown">
                  <div className="p-2 border-b bg-slate-50">
                    <p className="text-xs text-slate-500 font-medium px-2">Switch Role</p>
                  </div>
                  <div className="p-1.5">
                    {DEMO_ACCOUNTS.map((account) => {
                      const RoleIcon = account.icon;
                      const isActive = account.role === user.role;
                      const isLoading = switching === account.role;
                      return (
                        <button
                          key={account.role}
                          onClick={() => switchRole(account)}
                          disabled={!!switching}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                            isActive ? 'bg-teal-50 border border-teal-200' : 'hover:bg-slate-50 border border-transparent'
                          } ${isLoading ? 'opacity-60' : ''}`}
                          data-testid={`demo-switch-${account.role}`}
                        >
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${account.color} flex items-center justify-center shrink-0`}>
                            <RoleIcon size={14} className="text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-800">{account.label}</p>
                            <p className="text-[10px] text-slate-500 truncate">{account.name}</p>
                          </div>
                          {isLoading && <RefreshCw size={14} className="text-teal-600 animate-spin" />}
                          {isActive && !isLoading && (
                            <span className="text-[10px] font-medium text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded">Active</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={exitDemo} className="text-xs text-slate-400 hover:text-white transition-colors px-2 py-1" data-testid="demo-exit-button">
            Exit Demo
          </button>
          <button onClick={() => setDismissed(true)} className="text-slate-500 hover:text-white p-1" data-testid="demo-dismiss">
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
